import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Brand from "@/components/Brand";
import SiteHead from "@/components/SiteHead";
import api, { mediaUrl } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { BRAND_NAME, FOUNDER_NAME, FOUNDER_SAME_AS } from "@/lib/brand";

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null);
    setErr(false);
    api.get(`/blog/posts/${slug}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen lp-paper flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-stone-600">This article is not published.</p>
          <Link to="/blog" className="text-orange-700 font-semibold underline">Back to blog</Link>
        </div>
      </div>
    );
  }
  if (!data?.post) {
    return <div className="min-h-screen lp-paper" />;
  }

  const p = data.post;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: p.title,
    description: p.meta_description || p.excerpt,
    image: p.cover_image ? [mediaUrl(p.cover_image)] : undefined,
    datePublished: p.published_at,
    dateModified: p.updated_at || p.published_at,
    author: { "@type": "Person", name: p.author || FOUNDER_NAME, sameAs: FOUNDER_SAME_AS },
    publisher: { "@type": "Organization", name: BRAND_NAME },
    mainEntityOfPage: `${origin}/blog/${p.slug}`,
    keywords: p.meta_keywords || (p.tags || []).join(", "),
    inLanguage: "en-IN",
  };

  return (
    <div className="lp-paper text-stone-900 min-h-screen font-sans antialiased">
      <SiteHead
        title={p.meta_title || `${p.title} · ${BRAND_NAME}`}
        description={p.meta_description || p.excerpt}
        path={`/blog/${p.slug}`}
        image={p.cover_image}
        type="article"
        faq={p.faq}
        extraJsonLd={articleLd}
        published={p.published_at}
        modified={p.updated_at}
        author={p.author}
        keywords={p.meta_keywords}
      />
      <header className="sticky top-0 z-50 border-b border-stone-900/10 bg-[#FBF7F0]/85 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Brand variant="light" size="xl" /></Link>
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-stone-900"><ArrowLeft className="w-4 h-4" /> Blog</Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">{p.category} · {fmt(p.published_at)} · {p.reading_minutes} min read</p>
        {p.cover_image ? (
          <img src={mediaUrl(p.cover_image)} alt={p.cover_alt || p.title} className="mt-6 w-full rounded-2xl border-2 border-stone-900 object-cover max-h-[420px]" />
        ) : null}
        <div className="blog-prose mt-6" data-testid="blog-article" dangerouslySetInnerHTML={{ __html: p.body_html }} />

        {p.faq?.length > 0 && (
          <section className="mt-12 border-t-2 border-stone-900 pt-8">
            <h2 className="font-display text-2xl font-extrabold">Frequently asked questions</h2>
            <dl className="mt-4 space-y-4">
              {p.faq.map((f, i) => (
                <div key={i} className="rounded-xl bg-white border-2 border-stone-900 p-4">
                  <dt className="font-semibold">{f.q}</dt>
                  <dd className="mt-1 text-stone-600 text-sm leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <div className="mt-12 rounded-2xl bg-orange-500 border-2 border-stone-900 lp-hard p-6 sm:p-8 text-white">
          <h2 className="font-display text-2xl font-extrabold">Launch your store this week</h2>
          <p className="mt-2 text-white/90">Website from ₹1999/year — cart, COD, UPI and SEO included.</p>
          <Link to="/signup?plan=website" className="mt-4 inline-block rounded-full bg-white text-stone-900 font-bold px-5 py-2.5 border-2 border-stone-900">Get Started</Link>
        </div>

        {data.related?.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-xl font-extrabold">Keep reading</h2>
            <ul className="mt-3 space-y-2">
              {data.related.map((r) => (
                <li key={r.slug}><Link to={`/blog/${r.slug}`} className="text-orange-800 font-semibold hover:underline">{r.title}</Link></li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}
